import { useMemo } from "react";

import type { LoaderFunctionArgs } from "@remix-run/node";
import { type Navigation, useMatches } from "@remix-run/react";

import type { User } from "@prisma/client";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

const DEFAULT_REDIRECT = "/";

/**
 * This should be used any time the redirect path is user-provided
 * (Like the query string on our login/signup pages). This avoids
 * open-redirect vulnerabilities.
 * @param {string} to The redirect destination
 * @param {string} defaultRedirect The redirect to use if the to is unsafe.
 */
export function safeRedirect(
  to: FormDataEntryValue | string | null | undefined,
  defaultRedirect: string = DEFAULT_REDIRECT,
) {
  if (!to || typeof to !== "string") {
    return defaultRedirect;
  }

  if (!to.startsWith("/") || to.startsWith("//")) {
    return defaultRedirect;
  }

  return to;
}

/**
 * This base hook is used in other hooks to quickly search for specific data
 * across all loader data using useMatches.
 * @param {string} id The route id
 * @returns {JSON|undefined} The router data or undefined if not found
 */
export function useMatchesData(
  id: string,
): Record<string, unknown> | undefined {
  const matchingRoutes = useMatches();
  const route = useMemo(
    () => matchingRoutes.find((route) => route.id === id),
    [matchingRoutes, id],
  );
  return route?.data as Record<string, unknown>;
}

function isUser(user: any): user is User {
  return user && typeof user === "object" && typeof user.email === "string";
}

export function useOptionalUser(): User | undefined {
  const data = useMatchesData("root");
  if (!data || !isUser(data.user)) {
    return undefined;
  }
  return data.user;
}

export function useUser(): User {
  const maybeUser = useOptionalUser();
  if (!maybeUser) {
    throw new Error(
      "No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.",
    );
  }
  return maybeUser;
}

export function validateEmail(email: unknown): email is string {
  return typeof email === "string" && email.length > 3 && email.includes("@");
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const parseSearchParams = (
  request: LoaderFunctionArgs["request"],
): URLSearchParams => {
  return new URL(request.url).searchParams;
};

export function isValidDate(dateStr: string) {
  const regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
  return regex.test(dateStr);
}

export function convertDateToUTC(date: Date) {
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    ),
  );
}

export function isSubmittingAndRedirecting(navigation: Navigation) {
  return (
    navigation.state === "submitting" ||
    (navigation.state === "loading" &&
      !!navigation.formMethod &&
      navigation.formAction !== navigation.location.pathname)
  );
}

export const isEmpty = (obj: any) =>
  obj === false
    ? false
    : [Object, Array, Set].includes((obj || {}).constructor) &&
      !Object.entries(obj || {}).length;

export const getErrorMessage = (error: unknown): string | undefined => {
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
};

export const getErrorCode = (error: unknown): number | undefined => {
  if (
    typeof error === "object" &&
    error !== null &&
    "data" in error &&
    typeof error.data === "object" &&
    error.data !== null
  ) {
    if ("error" in error.data && typeof error.data.error === "number") {
      return error.data.error;
    }
  }
  if (typeof error === "number") {
    return error;
  }
};

export function typedObjectEntries<T extends Record<string, any>>(
  obj: T,
): [keyof T, T[keyof T] | unknown][] {
  return Object.entries(obj) as [keyof T, T[keyof T] | unknown][];
}

export const assertNever = (value: never): never => {
  throw new Error(`Unexpected value: ${value}`);
};

export const isPWAInstalled = () => {
  return (
    typeof document !== "undefined" &&
    window.matchMedia("(display-mode: standalone)").matches
  );
};

export const range = (length: number) => {
  return Array.from({ length }, (_, i) => i);
};

export const isDarkMode = () =>
  typeof document !== "undefined" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;
